import React, { useState, useMemo } from 'react'
import { Modal } from 'react-bootstrap'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import { HighRiskPermissionBadge, SuperUserPermissionBadge } from 'shared/badges'
import EmployeePermissionDropdown from './PermissionDropdown'
import PermissionValueDropdown from 'duties/shared/PermissionValueDropdown'
import { useRoleEmployees } from 'api/hooks/use-role-employees'
import { usePermissionRoles } from 'api/hooks/use-permission-roles'
import { usePermission } from 'api/hooks/use-permissions'
import TableLoading from 'shared/tables/table-loading'
import TableFooter from 'shared/tables/table-footer'
import { faMagic } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { InternalConflict } from 'shared/badges'

function AddPermission({ setShow, show, employee }) {
  const [params, setParams] = useState({
    permission_id: '',
    permission_value_id: '',
    status: 'active',
    page: 1,
    perPage: 25,
    isExpansive: true
  })

  const {
    data: permissionRoles,
    isSuccess: rolesSuccess,
    isLoading: rolesLoading
  } = usePermissionRoles({
    params,
    options: { enabled: !!params.permission_id }
  })

  const { data: selectedPermission, isSuccess: permissionSuccess } =
    usePermission({
      params: { id: params.permission_id },
      options: { enabled: !!params.permission_id }
    })

  const filteredRoles = useMemo(() => {
    if (!rolesSuccess) return []
    return permissionRoles.data
  }, [rolesSuccess, permissionRoles])

  const bestChoiceRoleId = useMemo(() => {
    if (!rolesSuccess) return null
    if (!permissionRoles?.data?.length) return null
    const sorted = [...permissionRoles.data].sort((a, b) => {
      const aVal = a?.problem_sum ?? Infinity
      const bVal = b?.problem_sum ?? Infinity
      return aVal - bVal
    })
    return sorted[0]?.id
  }, [rolesSuccess, permissionRoles])

  const displayCaption = (caption) => {
    return caption ? ` - ${caption}` : ''
  }

  const permissionValueCaption = (value) => {
    if (!permissionSuccess || !selectedPermission) return ''
    const pv = selectedPermission.permission_values.find(
      (v) => v.value === value
    )
    return pv ? pv.value_caption : ''
  }

  const handleClose = () => {
    setShow(false)
  }

  const RoleRow = ({ role }) => {
    const { data, isSuccess, isLoading } = useRoleEmployees({
      params: {
        role_id: role.id,
        active: 'true',
        perPage: 1,
        page: 1
      }
    })

    if (isLoading) {
      return <TableLoading columns={2} />
    }

    const otherUsers = isSuccess ? Math.max(data.meta.total - 1, 0) : 0

    return (
      <tr key={`employee-role-${role.id}`}>
        <td>{role.name}</td>
        <td className="text-center">{otherUsers}</td>
      </tr>
    )
  }

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Add Permission for {employee.external_id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="bg-white pb-2 pt-2">
          <Col sm={1}></Col>
          <Col xs={10}>
            <div>Select the permission you want to add</div>
            <div className="mt-2">
              <Row>
                <Col md={8} className="mb-2 mb-md-0">
                  <EmployeePermissionDropdown
                    employeeId={employee.id}
                    params={params}
                    setParams={setParams}
                  />
                </Col>
                <Col md={4}>
                  <PermissionValueDropdown
                    permissionId={params.permission_id}
                    params={params}
                    setParams={setParams}
                  />
                </Col>
              </Row>
            </div>
          </Col>
          <Col sm={1}></Col>
        </Row>
        {permissionSuccess && selectedPermission?.long_description && (
          <Row className="mb-4 pb-4 border-bottom">
            <Col sm={1}></Col>
            <Col xs={10}>
              <Row>
                <Col sm={8}>
                  <div className="text-muted mt-2 small">
                    {selectedPermission.long_description}
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        )}

        {params.permission_id && (
          <>
            <Row className="mt-4">
              <Col sm={1}></Col>
              <Col sm={10}>
                <div className="mb-2">
                  <strong>
                    Option #1: Add existing Role that has this permission to the
                    user
                  </strong>
                </div>
                <div className="text-muted">Roles with this permission</div>
              </Col>
              <Col sm={1}></Col>
            </Row>

            <Row className="mt-4">
              <Col sm={1}></Col>
              <Col sm={10}>
                <Table bordered responsive size="sm">
                  <thead>
                    <tr>
                      <th>Role</th>
                      <th className="text-center">Permission Value</th>
                      <th className="text-center">Internal Conflicts</th>
                      <th className="text-center">High Risk</th>
                      <th className="text-center">Super User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rolesLoading && <TableLoading columns={5} />}
                    {rolesSuccess &&
                      filteredRoles.length === 0 &&
                      !rolesLoading && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted">
                            No roles found
                          </td>
                        </tr>
                      )}
                    {rolesSuccess &&
                      filteredRoles.map((role) => (
                        <tr key={`permission-role-${role.id}`}>
                          <td>
                            {role.name}
                            {role.id === bestChoiceRoleId && (
                              <>
                                <Badge pill className="badge-primary ms-2">
                                  <FontAwesomeIcon
                                    icon={faMagic}
                                    className="me-2"
                                  />
                                  Best Choice
                                  <small className="ms-1">(Beta)</small>
                                </Badge>
                              </>
                            )}
                            {employee.roles.some((r) => r.id === role.id) && (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>
                                    User is already assigned to this role
                                  </Tooltip>
                                }
                              >
                                <Badge pill className="badge-warning ms-2">
                                  Role Assigned
                                </Badge>
                              </OverlayTrigger>
                            )}
                          </td>
                          <td className="text-center">
                            {`${role.permission_value}${displayCaption(
                              permissionValueCaption(role.permission_value)
                            )}`}
                          </td>
                          <td className="text-center">
                            {role.internal_conflicts_count > 0 && (
                              <InternalConflict
                                count={role.internal_conflicts_count}
                              />
                            )}
                          </td>
                          <td className="text-center">
                            {role.high_risk_permissions_count > 0 && (
                              <HighRiskPermissionBadge
                                count={role.high_risk_permissions_count}
                              />
                            )}
                          </td>
                          <td className="text-center">
                            {role.superuser_permissions_count > 0 && (
                              <SuperUserPermissionBadge
                                count={role.superuser_permissions_count}
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
                {rolesSuccess && permissionRoles.meta.total > 25 && (
                  <div className="mt-4">
                    <TableFooter
                      meta={permissionRoles.meta}
                      params={params}
                      setParams={setParams}
                    />
                  </div>
                )}
              </Col>
              <Col sm={1}></Col>
            </Row>

            <Row className="mt-4">
              <Col sm={1}></Col>
              <Col sm={10}>
                <div className="mb-2">
                  <strong>
                    Option #2: Add permission to Role that the User currently
                    has.
                  </strong>
                </div>
                <div className="text-muted">
                  Here are the User's current Roles
                </div>
              </Col>
              <Col sm={1}></Col>
            </Row>

            <Row className="mt-4">
              <Col sm={1}></Col>
              <Col sm={10}>
                <Table bordered responsive size="sm">
                  <thead>
                    <tr>
                      <th>Role</th>
                      <th className="text-center">User Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employee.roles.length === 0 && (
                      <tr>
                        <td colSpan={2} className="text-center text-muted">
                          User has no roles
                        </td>
                      </tr>
                    )}
                    {employee.roles.map((role) => (
                      <RoleRow key={`employee-role-${role.id}`} role={role} />
                    ))}
                  </tbody>
                </Table>
              </Col>
              <Col sm={1}></Col>
            </Row>
          </>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default AddPermission
